/**
 * The offline / sync indicator — LV §127-132, Arch §15.
 *
 * Permanently visible in the header, because the one thing a technician needs to know
 * before walking away from a plant is whether their work has reached the server. A
 * pending count hidden behind a menu is a pending count nobody checks.
 *
 * Conflicts are shown as an alarm rather than a count: Arch §15 requires the user to be
 * able to *see the differences*, and a conflict that looks like a queue entry would sit
 * there until someone wondered why a number never went down.
 *
 * Absent entirely where `FEATURE_OFFLINE` is off (LV §181). "Synchronisiert" beside a
 * clock in an installation with no offline operation is a promise about a mechanism
 * that is not running — worse than no indicator at all.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CircleCheck,
  CircleX,
  CloudOff,
  CloudUpload,
  RefreshCw,
  TriangleAlert,
} from "lucide-react";

import { useFeatureFlags } from "@/app/useFeatureFlags";
import {
  getSyncStatus,
  onSyncStatus,
  synchronise,
  type SyncStatus,
} from "@/offline/sync";

export function SyncIndicator() {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  // Subscribed rather than read once: the header mounts before the flags arrive, and a
  // pill promising "Synchronisiert" in an installation with no sync engine is worse
  // than no pill at all (LV §181).
  const offline = useFeatureFlags().FEATURE_OFFLINE;

  useEffect(() => {
    if (!offline) return;
    void getSyncStatus().then(setStatus);
    return onSyncStatus(setStatus);
  }, [offline]);

  if (!offline) return null;
  if (!status) return null;

  const pending = status.counts.PENDING_UPLOAD + status.counts.LOCAL_ONLY;
  const conflicts = status.counts.CONFLICT;
  const errors = status.counts.ERROR;

  // Ordered by what needs attention first, not by state machine order: a conflict is
  // a decision waiting on a person, being offline is a fact to be aware of, and a
  // clean synced state needs no emphasis at all.
  if (conflicts > 0) {
    return (
      <Link
        to="/sync"
        className="sync-pill sync-pill--conflict"
        data-testid="sync-indicator"
        title={`${conflicts} Konflikt${conflicts === 1 ? "" : "e"} — Klärung erforderlich`}
        aria-label={`${conflicts} Konflikt${conflicts === 1 ? "" : "e"} — Klärung erforderlich`}
      >
        <TriangleAlert className="icon" aria-hidden="true" />
        <span className="sync-pill__count">{conflicts}</span>
      </Link>
    );
  }

  if (!status.online) {
    return (
      <Link
        to="/sync"
        className="sync-pill sync-pill--offline"
        data-testid="sync-indicator"
        title={pending > 0 ? `Offline · ${pending} wartend` : "Offline"}
        aria-label={pending > 0 ? `Offline, ${pending} wartend` : "Offline"}
      >
        <CloudOff className="icon" aria-hidden="true" />
        {pending > 0 ? <span className="sync-pill__count">{pending}</span> : null}
      </Link>
    );
  }

  if (status.syncing) {
    return (
      <span
        className="sync-pill sync-pill--syncing"
        data-testid="sync-indicator"
        title="Synchronisiere…"
        aria-label="Synchronisiere…"
      >
        <RefreshCw className="icon sync-pill__spin" aria-hidden="true" />
      </span>
    );
  }

  if (errors > 0) {
    return (
      <Link
        to="/sync"
        className="sync-pill sync-pill--conflict"
        data-testid="sync-indicator"
        title={`${errors} Übertragungsfehler`}
        aria-label={`${errors} Übertragungsfehler`}
      >
        <CircleX className="icon" aria-hidden="true" />
        <span className="sync-pill__count">{errors}</span>
      </Link>
    );
  }

  if (pending > 0) {
    return (
      <button
        type="button"
        className="sync-pill sync-pill--offline"
        data-testid="sync-indicator"
        onClick={() => void synchronise()}
        title={`${pending} wartend — jetzt übertragen`}
        aria-label={`${pending} wartend, jetzt übertragen`}
      >
        <CloudUpload className="icon" aria-hidden="true" />
        <span className="sync-pill__count">{pending}</span>
      </button>
    );
  }

  return (
    <span
      className="sync-pill sync-pill--online"
      data-testid="sync-indicator"
      title="Alle Änderungen übertragen"
      aria-label="Synchronisiert — alle Änderungen übertragen"
    >
      <CircleCheck className="icon" aria-hidden="true" />
    </span>
  );
}
